<template>
    <ElevatedCard :widthType="WidthType.third">
        <HeaderWidget
            :iconUrl="loadIcon"
            :sizeIcon="35"
            :subtitle="$t('requestInterventionWidget.subtitle')"
            :title="widgetTitle"
            @click="onPressHeader"
        />
        <ReglePastille v-if="widget" cle="dashboard.widgetPerimetreCommun" />
        <div v-if="widget" class="site-widget-container">
            <span v-if="widget.sites == null || widget.sites.length > 1" class="site-widget">{{
                $t('multisites')
            }}</span>
            <div v-for="site in widget.sites" v-else :key="site.id" class="site-widget-item">
                <div class="site-widget">{{ site.description }}</div>
            </div>
        </div>
        <div class="content-container">
            <NoData v-if="!isLoading && noData" textNoData="widget.intervention.noData" />
            <div v-else-if="isEnded" id="chart" class="chart-width">
                <PieChart
                    :key="isLoading"
                    :colors="[getColorEnCours(), getColorPlanifie(), getColorNonPlanifie(), getColorTraite()]"
                    :height="180"
                    :isLoading="isLoading"
                    :isPercentage="false"
                    :labels="[
                        $t('requestInterventionWidget.inProgress') + ' (' + getInterventionRequestData.nbEnCours + ')',
                        $t('requestInterventionWidget.planned') + ' (' + getInterventionRequestData.nbPlanifie + ')',
                        $t('requestInterventionWidget.unplanned') +
                            ' (' +
                            getInterventionRequestData.nbNonPlanifie +
                            ')',
                        $t('requestInterventionWidget.traitees') + ' (' + getInterventionRequestData.nbTraite + ')',
                    ]"
                    :seriesValues="series"
                    :showDataLabels="true"
                    :showLegend="true"
                    :width="420"
                    @dataPointClicked="naviguerVersRecherche"
                />
            </div>
        </div>
    </ElevatedCard>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import HeaderWidget from '@/components/ui-components/HeaderWidget.vue';
import ElevatedCard, { WidthType } from '@/components/ui-components/ElevatedCard.vue';
import { InterventionRequestAction } from '../store/modules/InterventionRequestWidget';
import NoData from '@/components/ui-components/NoDataWidget.vue';
import { DemandeInterventionActions } from '@/modules/DemandeIntervention/store/action-types';
import { EtatDemandeIntervention } from '@/models/DemandeIntervention';
import { store } from '@/plugins/store';
import { Widget } from '@/models/Widget';
import NavFilter, { Site } from '@/models/NavFilter';
import PieChart from '@/components/ui-components/PieChart.vue';
import RequestInterventionWidgetItems from '@/models/RequestInterventionWidgetItems';
import { EquipementAction } from '@/modules/Equipements/store/actions-types';
import ReglePastille from '@/components/regles/ReglePastille.vue';

export default defineComponent({
    name: 'InterventionRequest',
    components: { PieChart, HeaderWidget, ElevatedCard, NoData, ReglePastille },
    props: {
        widget: { type: Object as PropType<Widget>, required: false },
        equipementDemandeInterventionData: {
            type: Object as PropType<RequestInterventionWidgetItems>,
            required: false,
        },
        equipementDemandeInterventionDataLoading: { type: Boolean, default: false },
        isBim: { type: Boolean, default: false },
        title: { type: String, required: false },
    },
    setup() {
        return {
            WidthType,
        };
    },
    data() {
        return {
            isEnded: false,
            series: [0, 0, 0, 0],
        };
    },
    watch: {
        equipementDemandeInterventionData: {
            handler(newData) {
                if (newData) {
                    this.series = [newData.nbEnCours, newData.nbPlanifie, newData.nbNonPlanifie, newData.nbTraite];
                    this.isEnded = true;
                }
            },
            immediate: true,
        },
        isLoading(loading) {
            if (!loading && this.equipementDemandeInterventionData) {
                this.isEnded = true;
            }
        },
    },
    async beforeMount() {
        if (this.equipementDemandeInterventionData) {
            this.series = [
                this.equipementDemandeInterventionData.nbEnCours,
                this.equipementDemandeInterventionData.nbPlanifie,
                this.equipementDemandeInterventionData.nbNonPlanifie,
                this.equipementDemandeInterventionData.nbTraite,
            ];
            this.noData =
                this.equipementDemandeInterventionData.nbEnCours === 0 &&
                this.equipementDemandeInterventionData.nbPlanifie === 0 &&
                this.equipementDemandeInterventionData.nbNonPlanifie === 0 &&
                this.equipementDemandeInterventionData.nbTraite === 0;
            this.isEnded = true;
            return;
        }
        let siteIds: number[] = this.widget?.sites ? this.widget?.sites.map(site => site.id) : [];
        if (siteIds.length === this.sitesTotal.length) {
            siteIds = [];
        }
        await this.$store
            .dispatch(`widgetDemandeIntervention/${InterventionRequestAction.GET_INTERVENTION_REQUEST_DATA}`, siteIds)
            .then((items: RequestInterventionWidgetItems) => {
                this.series = [items.nbEnCours, items.nbPlanifie, items.nbNonPlanifie, items.nbTraite];
                this.noData =
                    items.nbEnCours === 0 &&
                    items.nbPlanifie === 0 &&
                    items.nbNonPlanifie === 0 &&
                    items.nbTraite === 0;
            });
        this.isEnded = true;
    },
    methods: {
        getColorEnCours() {
            return getComputedStyle(document.documentElement).getPropertyValue('--en-cours');
        },
        getColorPlanifie() {
            return getComputedStyle(document.documentElement).getPropertyValue('--planifie');
        },
        getColorNonPlanifie() {
            return getComputedStyle(document.documentElement).getPropertyValue('--non-planifie');
        },
        getColorTraite() {
            return getComputedStyle(document.documentElement).getPropertyValue('--fait');
        },
        onChartClick(event: any, config: any, chartContext: any) {
            const index = chartContext.dataPointIndex;
            if (index !== -1) {
                this.naviguerVersRecherche(index);
            }
        },
        onPressHeader() {
            if (this.isBim) {
                this.$store.dispatch(`equipementState/${EquipementAction.DEMANDES_INTERVENTION_NAV_FILTER}`, {});
                return;
            }
            if (!store.getters.getUserInformations.fonctions.includes('consulter.demande')) {
                return;
            }
            const filter: NavFilter = {
                sites: this.widget?.sites ?? [],
                status: [],
            };
            this.$store.dispatch(`suiviDemandeInterventionModuleState/${DemandeInterventionActions.NAV_FILTER}`, filter, {
                root: true,
            });
            this.$router.push('/suivi-demande-intervention');
        },
        naviguerVersRecherche(dataPointIndex: number) {
            if (this.isBim) {
                let status: EtatDemandeIntervention | undefined;
                if (dataPointIndex === 0) status = EtatDemandeIntervention.enCours;
                else if (dataPointIndex === 1) status = EtatDemandeIntervention.planifiee;
                else if (dataPointIndex === 2) status = EtatDemandeIntervention.nonPlanifiee;
                else if (dataPointIndex === 3) status = EtatDemandeIntervention.traitee;

                this.$store.dispatch(`equipementState/${EquipementAction.DEMANDES_INTERVENTION_NAV_FILTER}`, {
                    status: status ? [status] : [],
                });
                return;
            }
            if (!store.getters.getUserInformations.fonctions.includes('consulter.demande')) {
                return;
            }
            const sites: Site[] = this.widget?.sites ?? [];
            if (dataPointIndex === 0) {
                const filter: NavFilter = {
                    sites: sites,
                    status: [EtatDemandeIntervention.enCours],
                };
                this.$store.dispatch(
                    `suiviDemandeInterventionModuleState/${DemandeInterventionActions.NAV_FILTER}`,
                    filter,
                    { root: true }
                );
            } else if (dataPointIndex === 1) {
                const filter: NavFilter = {
                    sites: sites,
                    status: [EtatDemandeIntervention.planifiee],
                };
                this.$store.dispatch(
                    `suiviDemandeInterventionModuleState/${DemandeInterventionActions.NAV_FILTER}`,
                    filter,
                    { root: true }
                );
            } else if (dataPointIndex === 2) {
                const filter: NavFilter = {
                    sites: sites,
                    status: [EtatDemandeIntervention.nonPlanifiee],
                };
                this.$store.dispatch(
                    `suiviDemandeInterventionModuleState/${DemandeInterventionActions.NAV_FILTER}`,
                    filter,
                    { root: true }
                );
            } else if (dataPointIndex === 3) {
                const filter: NavFilter = {
                    sites: sites,
                    status: [EtatDemandeIntervention.traitee],
                };
                this.$store.dispatch(
                    `suiviDemandeInterventionModuleState/${DemandeInterventionActions.NAV_FILTER}`,
                    filter,
                    { root: true }
                );
            }

            this.$router.push('/suivi-demande-intervention');
        },
    },
    computed: {
        sitesTotal(): Site[] {
            return store.getters['siteList/getListValue'];
        },
        isLoading() {
            return this.widget
                ? this.$store.state.homeState.widgetDemandeIntervention.isLoading
                : this.equipementDemandeInterventionDataLoading;
        },
        noData() {
            const data = this.getInterventionRequestData;
            return (
                (data.nbEnCours || 0) + (data.nbPlanifie || 0) + (data.nbNonPlanifie || 0) + (data.nbTraite || 0) === 0
            );
        },
        getInterventionRequestData() {
            return this.equipementDemandeInterventionData || this.$store.getters.getInterventionRequest;
        },
        loadIcon() {
            return require('@/assets/img/menu/wheel.png');
        },
        widgetTitle() {
            return this.title || (this.widget ? this.widget.libelle : '');
        },
    },
});
</script>

<style scoped>
.content-container {
    background-color: var(--white);
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    padding: 0 20px;
}
.site-widget-item {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
}
.site-contract {
    font-size: 11px;
    color: var(--dark-grey);
}
@media screen and (max-height: 800px) {
    .content-container {
        height: 73%;
    }
}
</style>
